import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import React, { useEffect, useState } from 'react';
import { Mastery } from 'services';
import capitalise from 'utils/capitalise';
import useErrorCatcher from "utils/useErrorCatcher";
import useRouter from 'utils/useRouter';
import validate from "validate.js";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  datepicker: {
    marginTop: 0
  }
}));

const schema = {
  name_en: {
    presence: { allowEmpty: false, message: "is required" },
  },
  code: {
    presence: { allowEmpty: false, message: "is required" }
  },
  xp_complete: {
    presence: { allowEmpty: false, message: "is required" },
    numericality: {
      greaterThan: 0
    }
  },
};

const INIT_VALUES = {
  name_en: "",
  name_zh: "",
  description_en: "",
  description_zh: "",
  code: "",
  comp_module_req: 0,
  elec_module_req: 0,
  xp_complete: 0,
};
let mounted = false;

const CreateModal = props => {
  let { onCreate, open, onClose, className, ...rest } = props;

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && capitalise(error.message)));
  const [errorMessage, setErrorMessage] = useState(null);
  const router = useRouter();

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...INIT_VALUES },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    setFormState({
      isValid: false,
      values: { ...INIT_VALUES },
      touched: {},
      errors: {}
    });
  }, [open]);

  useEffect(() => {
    mounted = true;

    return () => { mounted = false; }
  }, []);

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  if (!open) {
    return null;
  }


  const handleSubmit = event => {
    event.preventDefault();

    setFormState(formState => ({
      ...formState,
      touched: {
        // to show error message
        name_en: true,
        code: true,
        xp_complete: true,
      }
    }))
    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const { name_en, name_zh, code, description_en, description_zh, comp_module_req, elec_module_req, xp_complete } = formState.values;
      let data = { name_en, name_zh, code, description_en, description_zh, comp_module_req, elec_module_req, xp_complete };
      const { model } = await Mastery.create(data);
      onCreate();
      router.history.push(`/masteries/${model.id}/summary`);
      // onClose();
    }).finally(() => {
      mounted && setLoading(false);
    });

  }

  const handleFieldChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Create Mastery
            </Typography>
            <ErrorMessage className={classes.error} message={errorMessage} />
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={6} xs={6}>
                <CustTextField name="name_en" label="Name" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="name_zh" label="Name (ZH)" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="code" label="Code" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField name="description_en" label="Description" formState={formState} handleFieldChange={handleFieldChange} multiline />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField name="description_zh" label="Description (ZH)" formState={formState} handleFieldChange={handleFieldChange} multiline />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="comp_module_req" label="Compulsory Modules Required" formState={formState} handleFieldChange={handleFieldChange} type="number" />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="elec_module_req" label="Elective Modules Required" formState={formState} handleFieldChange={handleFieldChange} type="number" />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="xp_complete" label="XP to Complete" formState={formState} handleFieldChange={handleFieldChange} type="number" />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}



const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required, multiline } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      multiline={multiline}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default CreateModal;